/**
 * @fileoverview Type definitions for the firewall onboarding process.
 * This file contains all interfaces and types used in the firewall onboarding flow.
 */

/**
 * Represents a project (or codebase) with a unique ID and name.
 */
export interface Project {
    id: number;
    name: string;
    uuid?: string;
}

/**
 * Interface for the onboarding data object.
 */
export interface OnboardingData {
    // State Properties
    step: string;
    completed: boolean;
    github_api_key: string;
    loading: boolean;
    copied: string;
    hasExistingCodebases: boolean;
    projects: any[];
    installComplete: boolean;
    email: string;
    scanComplete: boolean;
    cliLastAuth: string;
    firewallAuthenticated: boolean;
    firewallConfigured: boolean;
    localCodebaseSetup: boolean;
    codebaseAdded: boolean;
    showCICDSetup: boolean;
    selectedTab: string;
    installMethod: string;
    _firewallConfigPollingInterval: NodeJS.Timeout | null;
    _cliAuthPollingInterval: NodeJS.Timeout | null;
    _codebasePollingInterval: NodeJS.Timeout | null;
    isTransitioning: boolean;
    activeCard: string;
    $watch: (property: string, callback: (newValue: any, oldValue: any) => void) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $dispatch: (event: string, detail?: any) => void;
    getCsrfToken: () => string;

    // Utility methods
    formatDate(dateString: string): string;
    getStep(): number;

    // UI state update methods
    setStep(step: string | number): Promise<void>;
    copyCommand(command: string): Promise<void>;
    nextStep(): Promise<void>;
    restart(): Promise<void>;
    skipOnboarding(): Promise<void>;

    // Authentication & Firewall methods
    setFirewallAuthenticated(value: boolean): void;
    setFirewallConfigured(value: boolean): void;
    setupCliAuthenticationListener(): void;
    setInstallMethod(method: string): void;
    skipFirewallSetup(): Promise<void>;
    startFirewallConfigPolling(): void;

    // Codebase methods
    setShowCICDSetup(value: boolean): void;

    // Step-specific methods
    initStep3(): Promise<void>;
    initStep4(): Promise<void>;

    // Finalization / Redirection methods
    finalizeOnboarding(): Promise<void>;

    // Initialization
    init(): void;

    // Status checking methods
    checkFirewallStatus(): Promise<void>;

    // Card transition methods
    startCardTransition(): void;
    endCardTransition(): void;
    proceedToConfigurationCard(): void;
    updateActiveCard(): void;

    // Codebase setup methods
    setLocalCodebaseSetup(value: boolean): void;
    simulateCodebaseAdded(): void;
    markInstallComplete(): void;
    setScanComplete(value: boolean): void;

    // Polling methods
    pollCliAuthStatus(): Promise<void>;
    startCliAuthPolling(): void;
    stopCliAuthPolling(): void;
    stopFirewallConfigPolling(): void;
    pollCodebaseStatus(): Promise<void>;
    startCodebasePolling(): void;
    stopCodebasePolling(): void;
}

/**
 * Mapping between step names and step numbers.
 */
export const stepMap: { [key: string]: number } = {
    learn: 1,
    you: 2,
    codebases: 3,
    ai_assistants: 4
};
